import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  PersonAdd as PersonAddIcon,
  Payment as PaymentIcon,
  Monitor as MonitorIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  BeachAccess as BeachAccessIcon,
  Report as ReportIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';

const drawerWidth = 240;

// Menu items for different user roles
const adminMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Register Customer', icon: <PersonAddIcon />, path: '/customer-registration' },
  { text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
  { text: 'Monitoring', icon: <MonitorIcon />, path: '/monitoring' },
  { text: 'Schedules', icon: <ScheduleIcon />, path: '/schedules' },
  { text: 'Caregiver Circles', icon: <GroupIcon />, path: '/caregiver-circles' },
  { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
  { text: 'Advanced Alerts', icon: <NotificationsIcon />, path: '/advanced-alerts' },
  { text: 'Advanced Reports', icon: <AssessmentIcon />, path: '/advanced-reports' },
  { text: 'Performance', icon: <SpeedIcon />, path: '/performance' },
  { text: 'Enhanced Vacation', icon: <BeachAccessIcon />, path: '/enhanced-vacation' },
  { text: 'Reports', icon: <ReportIcon />, path: '/reports' },
  { text: 'Admin', icon: <AdminIcon />, path: '/admin' },
];

const endUserMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'My Loved Ones', icon: <GroupIcon />, path: '/my-loved-ones' },
  { text: 'Schedules', icon: <ScheduleIcon />, path: '/schedules' },
  { text: 'Care Circle', icon: <GroupIcon />, path: '/care-circle' },
  { text: 'My Profile', icon: <PersonIcon />, path: '/my-profile' },
];

const Sidebar: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isEndUser } = useRole();
  const location = useLocation();

  if (!isAuthenticated) {
    return null;
  }

  // Get menu items based on user role (non-admins always get end-user nav)
  const getMenuItems = () => {
    if (isAdmin) {
      return adminMenuItems;
    }
    // Authenticated non-admins (including role still resolving) get end-user nav
    if (isEndUser || isAuthenticated) {
      return endUserMenuItems;
    }
    return [];
  };

  const menuItems = getMenuItems();

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          marginTop: '64px', // Height of the AppBar
        },
      }}
    >
      <Box sx={{ overflow: 'auto' }}>
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? 'white' : 'inherit',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
